import type {ReactElement} from 'react';
import {getAssetUrl} from '@Utils/s3.util';
import {Text, TextSize} from '@Components/text';
import type {PlatformPostPreviewProps} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {
  getStripMediaLayout,
  PreviewMediaFit,
} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {PreviewAccountHeader} from '../preview-account-header/preview-account-header';
import {PreviewAccountMetaSkeleton} from '../preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton';
import {PREVIEW_ACCOUNT_META_SQUARE_ONLY} from '../preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton.types';
import {PreviewAccountHeaderLayout} from '../preview-account-header/preview-account-header.types';
import {PreviewClampedCaption} from '../preview-clamped-caption/preview-clamped-caption';
import {PreviewAssetIcon} from '../preview-asset-icon/preview-asset-icon';
import {THREADS_ACTION_ICON_PATHS, THREADS_FOLLOW_ICON_PATH} from './threads-post-preview.types';
import styles from './threads-post-preview.module.scss';
import {getCaptionFromPublishingParams} from '@Libraries/social-media-params.library';

export function ThreadsPostPreview({account, platformMediaType, publishingParams, numberOfPages, renderMedia}: PlatformPostPreviewProps): ReactElement {
  const getCaption = (): string => {
    return getCaptionFromPublishingParams(publishingParams, account.id, platformMediaType);
  };

  const getCaptionIfWritten = (): ReactElement | null => {
    const captionText = getCaption();

    if (captionText === '') {
      return null;
    }

    return (
      <div className={styles.threadsCaptionHolder}>
        <PreviewClampedCaption captionText={captionText} textClassName={styles.threadsCaptionClamp}>
          <Text className={styles.threadsCaptionText} size={TextSize.XS} val={captionText} />
        </PreviewClampedCaption>
      </div>
    );
  };

  const getFollowBadge = (): ReactElement => {
    return (
      <span className={styles.threadsFollowBadge}>
        <img alt="" aria-hidden="true" className={styles.threadsFollowBadgeIcon} src={getAssetUrl(THREADS_FOLLOW_ICON_PATH)} />
      </span>
    );
  };

  const getHeader = (): ReactElement => {
    return (
      <div className={styles.threadsHeader}>
        <PreviewAccountHeader
          account={account}
          identityClassName={styles.threadsIdentity}
          layout={PreviewAccountHeaderLayout.INLINE}
          metaRow={<PreviewAccountMetaSkeleton parts={PREVIEW_ACCOUNT_META_SQUARE_ONLY} />}
          trailingAction={null}
        />
        {getFollowBadge()}
      </div>
    );
  };

  const getTextBlock = (): ReactElement => {
    return (
      <div className={styles.threadsTextBlock}>
        {getHeader()}
        {getCaptionIfWritten()}
      </div>
    );
  };

  const getMedia = (): ReactElement => {
    return renderMedia({layout: getStripMediaLayout(numberOfPages), fit: PreviewMediaFit.COVER, frameClassName: styles.threadsMediaFrame});
  };

  const getActionBar = (): ReactElement => {
    return (
      <div className={styles.threadsActionBar}>
        {THREADS_ACTION_ICON_PATHS.map((iconPath: string): ReactElement => {
          return <PreviewAssetIcon iconPath={iconPath} key={iconPath} />;
        })}
      </div>
    );
  };

  return (
    <div className={styles.threadsPost}>
      <div className={styles.threadsBody}>
        {getTextBlock()}
        {getMedia()}
      </div>
      {getActionBar()}
    </div>
  );
}
